import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {
  Modal, Form, DatePicker, InputNumber,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { selectEditState, selectSubmitTaxRateRequest } from '../../../../state/ui/configuration/taxes/taxUiReducer';
import { selectTaxRateMap } from '../../../../state/domain/taxes/taxRateReducer';
import { cancelEditTaxRate, requestSaveTaxRate } from '../../../../state/ui/configuration/taxes/taxUiActions';
import { DATE_FORMAT, convertToUTCDate } from '../packageUtil';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';


const StartDateField = ({ form, startDate, disabled }) => {
  const intl = useIntl();
  const isDateDisabled = (currentDate) => {
    // DISABLE: If the current date is before now.
    if (convertToUTCDate(currentDate).isBefore(moment.utc())) return true;

    // ENABLE: If no date has been entered?
    const formDate = form.getFieldValue('taxRateStartDate');
    if (!formDate) return false;

    // DISABLE: If date is after form date.
    return currentDate.isAfter(formDate.clone().endOf('day'));
  };

  return (
    <Form.Item required label={<FormattedMessage id="startDate" />}>
      { form.getFieldDecorator('dateFrom', {
        initialValue: startDate ? moment.utc(startDate) : undefined,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <DatePicker
          disabled={disabled}
          style={{ width: '300px' }}
          disabledDate={isDateDisabled}
          format={DATE_FORMAT}
          // placeholder={intl.formatMessage({ id: 'selectOne' })}
        />,
      )}
    </Form.Item>
  );
};

const TaxRateField = ({ form, taxRate }) => {
  const intl = useIntl();
  const taxRatePercent = taxRate && (taxRate * 100);

  return (
    <Form.Item required label={<FormattedMessage id="taxRate" />}>
      { form.getFieldDecorator('taxRate', {
        initialValue: taxRatePercent,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <InputNumber
          style={{ width: '300px' }}
          step={1}
          precision={2}
          min={0}
          max={999.99}
          placeholder={intl.formatMessage({ id: 'enter' })}
        />,
      )}
    </Form.Item>
  );
};


const EditTaxRateView = Form.create({ name: 'edit-tax-rate' })(({
  form, taxRate, onSave, isSubmitting, saveValidationError, ...rest
}) => {
  const isEdit = !!taxRate;


  const submitForm = async () => {
    try {
      const values = await form.validateFields();

      const newTaxRate = {
        taxRate: values.taxRate / 100,
        dateFrom: taxRate ? taxRate.dateFrom : convertToUTCDate(values.dateFrom).startOf('day').toISOString(),
      };
      onSave(newTaxRate);
    } catch (_) {
      // Validation failed. Nothing for us to do.
    }
  };


  return (
    <Modal
      title={<FormattedMessage id={isEdit ? 'updateTaxRate' : 'createTaxRate'} />}
      okText={<FormattedMessage id="save" />}
      onOk={submitForm}
      confirmLoading={isSubmitting}
      {...rest}
    >
      <ViaValidationAlert validationError={saveValidationError} />
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
        <StartDateField startDate={taxRate && taxRate.dateFrom} form={form} disabled={isEdit} />
        <TaxRateField taxRate={taxRate && (taxRate.taxRate)} form={form} />
      </Form>
    </Modal>
  );
});

export default function EditTaxRate() {
  // Redux Actions
  const dispatch = useDispatch();
  const onSave = newTaxRate => dispatch(requestSaveTaxRate(newTaxRate));

  // Redux State
  const editState = useSelector(selectEditState);
  const taxRate = useSelector(selectTaxRateMap)[editState.date && new Date(editState.date).getTime()];
  const saveTaxRateRequest = useSelector(selectSubmitTaxRateRequest);


  return (
    <EditTaxRateView
      taxRate={taxRate}
      visible={editState.isEditing}
      onSave={onSave}
      isSubmitting={!!(saveTaxRateRequest && saveTaxRateRequest.isLoading)}
      saveValidationError={saveTaxRateRequest && saveTaxRateRequest.error}
      onCancel={() => dispatch(cancelEditTaxRate())}
    />
  );
}
